import React, { useEffect, useState } from 'react';

import BackToPageButton from '@/components/ui/buttons/BackToPageButton';
import Button from '@/components/ui/buttons/Button';
import FormattedOrganizationMembershipCode from '@/components/ui/formats/FormattedOrganizationMembershipCode';
import GradeClassNumberLabel from '@/components/ui/formats/GradeClassNumberLabel';
import Checkbox from '@/components/ui/forms/Checkbox';
import ErrorList from '@/components/ui/forms/ErrorList';
import useFetch from '@/hooks/common/useFetch';
import { type OrganizationMembershipFormValues } from '@/interfaces/organizationMembership';
import { type Student } from '@/interfaces/student';

import CSSModule from './OrganizationMembershipRegistrationConfirmForm.module.scss';

interface Props {
  studentInfo: Student;
  formValues: OrganizationMembershipFormValues;
  onGoBack: () => void;
  onComplete: () => void;
}

interface UIProps {
  studentInfo: Student;
  code: string;
  isOrganizationMember: boolean;
  onCheckboxChange: (checked: boolean) => void;
  buttonDisabled: boolean;
  error?: { message: string } | null;
  onSubmit: () => void;
  onGoBack: () => void;
}

export const UI: React.FC<UIProps> = ({
  studentInfo,
  code,
  isOrganizationMember,
  onCheckboxChange,
  buttonDisabled,
  error,
  onSubmit,
  onGoBack,
}) => {
  const { organization_name, name, grade, class_name, attendance_number } =
    studentInfo;

  return (
    <>
      <div
        className={
          CSSModule.OrganizationMembershipRegistrationConfirmForm__Description
        }
      >
        以下の内容で生徒の登録を行います。詳細をご確認ください。
      </div>
      <div className={CSSModule.OrganizationMembershipRegistrationConfirmForm}>
        <div
          className={
            CSSModule.OrganizationMembershipRegistrationConfirmForm__StudentInformation__OrganizationCode
          }
        >
          <FormattedOrganizationMembershipCode code={code} />
        </div>
        <div
          className={
            CSSModule.OrganizationMembershipRegistrationConfirmForm__StudentInformation
          }
        >
          <div
            className={
              CSSModule.OrganizationMembershipRegistrationConfirmForm__StudentInformation__OrganizationName
            }
          >
            {organization_name}
          </div>
          <GradeClassNumberLabel
            gradeLabel={grade}
            classNameLabel={class_name}
            attendanceNumberLabel={attendance_number}
          />
          <div
            className={
              CSSModule.OrganizationMembershipRegistrationConfirmForm__StudentInformation__StudentName
            }
          >
            {name}
          </div>
        </div>

        <div
          className={
            CSSModule.OrganizationMembershipRegistrationConfirmForm__CheckBoxArea
          }
        >
          <Checkbox
            checked={isOrganizationMember}
            onChange={() => onCheckboxChange(!isOrganizationMember)}
            disabled={false}
            role="checkbox"
          >
            <div
              className={
                CSSModule.OrganizationMembershipRegistrationConfirmForm__Checkbox__Text
              }
            >
              私はこの生徒の保護者であることを確認しました
            </div>
          </Checkbox>
        </div>
      </div>
      <div className={CSSModule.SubmitButtonArea}>
        <ul className={CSSModule.SubmitButtonArea__Note}>
          <li>
            <p>
              生徒情報（年・組・番・氏名）が誤っている場合は、学校にお問い合わせするか、戻るボタンより団体会員コードを再設定してください
            </p>
          </li>
        </ul>
        {error && error.message.length > 0 && (
          <div className={CSSModule.SubmitButtonArea__Error}>
            <ErrorList errors={[error.message]} />
          </div>
        )}
        <Button
          className={CSSModule.SubmitButton}
          color="primary"
          type="submit"
          disabled={buttonDisabled}
          onClick={() => {
            onSubmit();
          }}
        >
          登録する
        </Button>
        <BackToPageButton
          onClick={onGoBack}
          className={CSSModule.SubmitButtonArea__BackToPageButton}
        >
          ＜戻る
        </BackToPageButton>
      </div>
    </>
  );
};

const OrganizationMembershipRegistrationConfirmForm: React.FC<Props> = ({
  studentInfo,
  formValues,
  onGoBack,
  onComplete,
}) => {
  const { code, first_name_kana, last_name_kana } = formValues;

  const [isOrganizationMember, setIsOrganizationMember] = useState(false);

  const { fetch, isFetching, error, status } = useFetch(
    'POST',
    '/api/school_communication/parent_organization_memberships',
  );

  const handleSubmit = () => {
    void fetch({
      body: {
        organization_membership: { code, first_name_kana, last_name_kana },
      },
    });
  };

  useEffect(() => {
    if (status === 201) {
      onComplete();
    }
  }, [status, onComplete]);

  const buttonDisabled = !isOrganizationMember || isFetching;

  return (
    <UI
      studentInfo={studentInfo}
      code={code}
      isOrganizationMember={isOrganizationMember}
      onCheckboxChange={setIsOrganizationMember}
      buttonDisabled={buttonDisabled}
      error={error}
      onSubmit={handleSubmit}
      onGoBack={onGoBack}
    />
  );
};

export default OrganizationMembershipRegistrationConfirmForm;
